import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ReactComponent as Logo } from '../../logo.svg';
import { Button } from '../../components/base-components';
import { cx, FOCUS_RING } from '../components/styles';
import { V2_LANDING } from '../nav';
import { ANCHORS, CONTAINER } from './landingConfig';
import { useLandingCtas } from './useLandingCtas';

/**
 * Sticky top bar. Mirrors the existing app header so the product feels
 * continuous. Below `md` the centre anchors disappear — three anchors are not
 * worth a drawer on a marketing page — but BOTH CTAs stay. "Sign in" used to be
 * hidden on phones too, which left an existing customer on mobile with no
 * visible way into the app short of the footer: the only button read "Start
 * free trial". Two small buttons fit beside the logo down to 360px.
 */
const ANCHOR_LINKS = [
  { label: 'Features', href: `#${ANCHORS.features}` },
  { label: 'How it works', href: `#${ANCHORS.how}` },
  { label: 'Plans', href: `#${ANCHORS.plans}` },
];

export const LandingNav: React.FC = () => {
  const { primary, secondary } = useLandingCtas();
  // The same page is served at `/` and at `/v2`. The logo goes back to
  // whichever one the visitor actually arrived on, so clicking it never moves
  // them to a different URL showing identical content.
  const { pathname } = useLocation();
  const home = pathname.startsWith(V2_LANDING) ? V2_LANDING : '/';
  return (
  <header className="sticky top-0 z-50 h-16 border-b border-white/10 bg-[#0a0a0a]/80 backdrop-blur-md">
    <div className={cx(CONTAINER, 'flex h-full items-center justify-between gap-4')}>
      <Link to={home} className={cx('flex items-center gap-2.5 rounded-lg', FOCUS_RING)}>
        <Logo style={{ width: '32px', height: '32px' }} />
        <span className="text-lg font-bold tracking-tight text-[#3b82f6]">FlashTools</span>
      </Link>

      <nav aria-label="Sections" className="hidden md:flex md:items-center md:gap-1">
        {ANCHOR_LINKS.map((link) => (
          <a
            key={link.href}
            href={link.href}
            className={cx(
              'rounded-lg px-3 py-2 text-sm text-white/60 transition-colors hover:text-white/90',
              FOCUS_RING,
            )}
          >
            {link.label}
          </a>
        ))}
      </nav>

      <div className="flex items-center gap-2">
        <Link to={secondary.to} className={cx('rounded-lg', FOCUS_RING)}>
          <Button variant="ghost" size="sm" whitespace="nowrap">
            {secondary.label}
          </Button>
        </Link>
        <Link to={primary.to} className={cx('rounded-lg', FOCUS_RING)}>
          <Button size="sm" whitespace="nowrap">
            {primary.label}
          </Button>
        </Link>
      </div>
    </div>
  </header>
  );
};
